import { lockScroll } from '@theme/utilities';
import { removeTrapFocus } from '@theme/focus';

/**
 * Valoraa: the cart drawer always opens as a modal overlay (Atolea's mini cart), at every width.
 *
 * Horizon's <theme-drawer> (assets/theme-drawer.js) opens non-modal from 990px up and squeezes the
 * page next to it. Its breakpoint is private, so instead of editing that core file we upgrade the
 * cart drawer's <dialog> to showModal() right after it opens (and after a breakpoint switch). That
 * gives the dimmed ::backdrop, the native focus trap and inert page at desktop too. The squeeze
 * margin is cancelled in CSS (snippets/vb-cart-styles.liquid) and the drawer has [no-persist], so
 * it is never restored as a sidebar after navigation. Closing still runs through ThemeDrawer
 * (Escape, backdrop click, close button), which unlocks the scroll it owns.
 */
const upgrade = () => {
  const drawer = document.getElementById('cart-drawer');
  const panel = drawer?.querySelector(':scope > dialog');
  if (!drawer || !(panel instanceof HTMLDialogElement)) return;
  if (!drawer.hasAttribute('open') || !panel.open) return;
  if (panel.matches(':modal')) return;

  const active = document.activeElement;
  removeTrapFocus();
  lockScroll(panel);
  panel.close();
  panel.showModal();

  const keep = active instanceof HTMLElement && panel.contains(active) ? active : null;
  const target = keep || panel.querySelector('.theme-drawer__close-button');
  if (target instanceof HTMLElement) target.focus({ preventScroll: true });
};

if (!(/** @type {any} */ (window).vbCartDrawerModal)) {
  /** @type {any} */ (window).vbCartDrawerModal = true;

  // ThemeDrawer dispatches this (bubbling) from the drawer right after show()/showModal()
  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target instanceof HTMLElement && event.target.id === 'cart-drawer') upgrade();
  });

  // ThemeDrawer switches to show() when the viewport crosses 990px; switch back after it runs
  window.matchMedia('(max-width: 989px)').addEventListener('change', () => setTimeout(upgrade, 0));

  // An older session may still have the drawer open as a restored sidebar
  customElements.whenDefined('theme-drawer').then(() => setTimeout(upgrade, 0));
}
